import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, Router } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import {
    AppSettings,
    LoggerClientService,
    LOGGER_CLIENT_SERVICE_IMPL,
    AUTH_SERVICE_IMPL,
    API_CLIENT_SERVICE_IMPL,
    ORDER_RETURN_SERVICE_IMPL,
    OrderReturnService,
    IAPIClientService,
    APIClientService,
    AuthenticationService,
    FlashMessageService,
    HttpStatusInterceptor,
    JwtInterceptor,
    SharedModule,
    ACCOUNTS_SERVICE_IMPL,
    AccountsService,
    TASKS_SERVICE_IMPL,
    TasksService,
    ORDERS_SERVICE_IMPL,
    OrdersService,
    GEOLOCATION_SERVICE_IMPL,
    GeolocationService,
    REALTIME_CLIENT_IMPL,
    RealtimeClientService,
    PROVIDER_SHOP_SERVICE_IMPL,
    ProviderShopService,
} from 'shared';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { AppPolicyRoles } from './app.policies.config';
import { MARKUP_CHECKOUT_SERVICE_IMPL } from '../services/markup-checkout/imarkup-checkout.service';
import { MarkupCheckoutService } from '../services/markup-checkout/markup-checkout.service';
import { PRODUCTPROVIDER_SERVICE_IMPL } from './modules/product-manager/services/iproductprovider.service';
import { ProductProviderService } from './modules/product-manager/services/productprovider.service';
import { EMPLOYEE_AUTH_SERVICE_IMPL } from 'pm-src/services/employee-auth/i-employee-auth.service';
import { EmployeeAuthService } from 'pm-src/services/employee-auth/employee-auth.service';
import { OFFLINEPRODUCT_SERVICE_IMPL, OfflineProductService } from 'shared';
import { AppSharedModule } from './modules/app-shared/app-shared.module';
import { EmployeeInterceptor } from './interceptors/employee.interceptor';
import { PMHttpStatusInterceptor } from './interceptors/pm-http-status.interceptor';
import { ErrorHandlerService } from 'projects/shared/src/lib/services/app-insights/error-handler.service';
import * as Sentry from '@sentry/angular-ivy';

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        IonicModule.forRoot(),
        AppRoutingModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
        }),
        TranslateModule.forRoot({
            defaultLanguage: AppSettings.get('defaultLocale'),
            loader: {
                provide: TranslateLoader,
                useFactory: translateLoaderFactory,
                deps: [HttpClient],
            },
        }),
        SharedModule,
        AppSharedModule,
        Sentry.TraceModule,
    ],
    providers: [
        {
            provide: RouteReuseStrategy,
            useClass: IonicRouteStrategy,
        },
        FlashMessageService,
        {
            provide: API_CLIENT_SERVICE_IMPL,
            useClass: APIClientService,
        },
        {
            provide: AUTH_SERVICE_IMPL,
            useFactory: (apiClient: IAPIClientService) => {
                return new AuthenticationService(
                    apiClient,
                    AppPolicyRoles,
                    'Provider-Manager-User'
                );
            },
            deps: [API_CLIENT_SERVICE_IMPL],
        },
        {
            provide: EMPLOYEE_AUTH_SERVICE_IMPL,
            useClass: EmployeeAuthService,
        },
        {
            provide: LOGGER_CLIENT_SERVICE_IMPL,
            useClass: LoggerClientService,
        },
        {
            provide: REALTIME_CLIENT_IMPL,
            useClass: RealtimeClientService,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpStatusInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: PMHttpStatusInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: EmployeeInterceptor,
            multi: true,
        },
        {
            provide: MARKUP_CHECKOUT_SERVICE_IMPL,
            useClass: MarkupCheckoutService,
        },
        {
            provide: ORDER_RETURN_SERVICE_IMPL,
            useClass: OrderReturnService,
        },
        {
            provide: GEOLOCATION_SERVICE_IMPL,
            useClass: GeolocationService,
        },
        {
            provide: TASKS_SERVICE_IMPL,
            useClass: TasksService,
        },
        {
            provide: ORDERS_SERVICE_IMPL,
            useClass: OrdersService,
        },
        {
            provide: PRODUCTPROVIDER_SERVICE_IMPL,
            useClass: ProductProviderService,
        },
        {
            provide: ACCOUNTS_SERVICE_IMPL,
            useClass: AccountsService,
        },
        {
            provide: OFFLINEPRODUCT_SERVICE_IMPL,
            useClass: OfflineProductService,
        },
        {
            provide: PROVIDER_SHOP_SERVICE_IMPL,
            useClass: ProviderShopService,
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}

export function translateLoaderFactory(http: HttpClient): MultiTranslateHttpLoader {
    return new MultiTranslateHttpLoader(http, [
        {
            prefix: AppSettings.get('backendBaseURL') + '/translations/get/2/',
            suffix: '',
        },
        {
            prefix: AppSettings.get('backendBaseURL') + '/translations/get/5/',
            suffix: '',
        },
    ]);
}
